import _, { Dictionary } from 'lodash';
import Vue from 'vue';
import {mapActions, mapGetters, mapState} from 'vuex';
import {required, maxLength, between, not, helpers} from 'vuelidate/lib/validators';
import EventHub from '../../lib/eventHub';
import {AxiosResponse} from 'axios';
import BioConfigService from '../../lib/services/bioConfigService';
import ClubService from '../../lib/services/clubService';
import TagsService from '../../lib/services/tagsService';
import TagsMuleService from '../../lib/services/tagsMuleService';
import AssetsService from '../../lib/services/assetsService';
import ContentFragmentService from '../../lib/services/contentFragmentService';
import {AlertNotificationModuleConstant, BioProfileModuleConstant, BioServicesConstant, EmployeeProfileModuleConstant, EventConstant, RoleConfigs, UrlModuleConstant} from '../../lib/constants';
import About from './edit-about.html';
import BioAdmin, { IRoleConfig, IBiosConfigResponse } from 'types/ba/bioAdmin';
import { rolesResponsibilities } from 'test/unit/responses/assetsServiceResponses.spec';

interface IComponentData {
	bioProfileServiceLoading: boolean;
	employeeId: string;
	countries: BioAdmin.ICountry[];
	clubs: BioAdmin.IProfileClub[];
	languages: BioAdmin.ITag[];
	tagGroups: BioAdmin.ITagGroup[];
	languagesTags: object;
	roles: string[];
	biosConfigResponse: BioAdmin.IBiosConfigResponse;
	roleFeatures: string[];
	jobTitles: BioAdmin.IJobTitle[];
	form: BioAdmin.IAboutMeFormValidator;
	certifications: BioAdmin.ICertification[];
	avatarCount: number;
	useSpringApi: boolean;
}

const bioConfigService: BioAdmin.IBioConfigService = new BioConfigService();
const clubService: BioAdmin.IClubService = new ClubService();
const tagsService: BioAdmin.ITagsService = new TagsService();
const assetsService: BioAdmin.IAssetsService = new AssetsService();
const tagsMuleService: BioAdmin.ITagsService = new TagsMuleService();
const contentFragmentService: BioAdmin.IContentFragmentService = new ContentFragmentService();

// custom validators
const textValidate = helpers.regex('textValidate', BioServicesConstant.textfieldRegexValidator);

export default Vue.component('about', {
	template: About,
	data(): IComponentData {
		return {
			bioProfileServiceLoading: true,
			employeeId: '',
			countries: [],
			clubs: [],
			languages: [],
			tagGroups: [],
			languagesTags: {},
			roles: [],
			biosConfigResponse:  {
				rolesFeatures: [
					{
						roleId: 1,
						roleName : 'Personal Trainer',
						features: [
							'contact',
							'quote'
						]
					}],
				features:
				{
					quote: {
						maxLength: '50',
					},
					contact: {}
				}
			},
			roleFeatures: [],
			jobTitles: [],
			certifications: [],
			avatarCount: 14,
			form: {
				quote: '',
				quoteCitation: '',
				aboutMe: '',
				clubs: [],
				avatarId: 1,
			},
			useSpringApi: false,
		};
	},
	computed: {
		...mapState(BioProfileModuleConstant.name, [
			'bioProfile',
		]),
		...mapGetters(BioProfileModuleConstant.name, {
			getClubs: BioProfileModuleConstant.getter.getClubs,
			getAvatarId: BioProfileModuleConstant.getter.getAvatarId,
			getLanguages: BioProfileModuleConstant.getter.getLanguages,
			getQuote: BioProfileModuleConstant.getter.getQuote,
			getQuoteCitation: BioProfileModuleConstant.getter.getQuoteCitation,
			getAboutMe: BioProfileModuleConstant.getter.getAboutMe,
			getJobTitles: BioProfileModuleConstant.getter.getJobTitles,
			getCertifications: BioProfileModuleConstant.getter.getCertifications,
		}),
		...mapGetters(UrlModuleConstant.name, {
			getUrlParameter: UrlModuleConstant.getter.getUrlParameter,
		}),
		hasValidRole(): boolean {
			return this.roles.length >= 1;
		},
		showPhoneNumber(): boolean {
			// show only if feature is on, is only a club lead, and a phone number exists
			return this.hasFeature('phone') && this.roles.includes(BioServicesConstant.clubLeadString) && this.roles.length === 1 && this.bioProfile.phoneNumber && this.bioProfile.phoneNumber.length > 0;
		},
	},
	methods: {
		...mapActions(AlertNotificationModuleConstant.name, {
			synchronizeAlertNotificationStore: AlertNotificationModuleConstant.action.synchronize,
		}),
		...mapActions(EmployeeProfileModuleConstant.name, {
			synchronizeEmployeeProfileStore: EmployeeProfileModuleConstant.action.synchronize,
		}),
		...mapActions(UrlModuleConstant.name, {
			synchronizeUrlStore: UrlModuleConstant.action.synchronize,
		}),
		...mapActions(BioProfileModuleConstant.name, {
			getProfile: BioProfileModuleConstant.action.getProfile,
			modifyProfileClubs: BioProfileModuleConstant.action.modifyProfileClubs,
			removeProfileClub: BioProfileModuleConstant.action.removeProfileClub,
			updateAvatarId: BioProfileModuleConstant.action.updateAvatarId,
			updateQuote: BioProfileModuleConstant.action.updateQuote,
			updateQuoteCitation: BioProfileModuleConstant.action.updateQuoteCitation,
			updateAboutMe: BioProfileModuleConstant.action.updateAboutMe,
			modifyProfileLanguages: BioProfileModuleConstant.action.modifyProfileLanguages,
			removeProfileLanguage: BioProfileModuleConstant.action.removeProfileLanguage,
		}),
		// COMPONENT METHODS
		getAllClubs() {
			return clubService.getClubLocations()
				.then((response: AxiosResponse<BioAdmin.IClubsResponse>) => {
					this.setAllClubs(response.data);
				});
		},
		setAllClubs(clubs: any) {
			const countries: BioAdmin.ICountry[] = [];
			// group the list of clubs by country
			const countryClubs: Dictionary<BioAdmin.IClub[]> = _.groupBy(clubs, 'country');
			_.forEach(countryClubs, (cValue, cKey) => {
				const stateProvinces: BioAdmin.IStateProvince[] = [];
				// group each country into states
				const stateClubs: Dictionary<BioAdmin.IClub[]> = _.groupBy(cValue, 'state');
				_.forEach(stateClubs, (sValue, sKey) => {
					stateProvinces.push({
						name: sKey,
						clubs: _.orderBy(sValue, 'clubTitle'),
					});
				});
				countries.push({
					name: cKey,
					stateProvinces: _.orderBy(stateProvinces, 'name'),
				});
			});
			this.countries = countries;
		},
		// get tags for dropdown lists
		getTagGroupsAndData() {
			if (this.useSpringApi)
				return tagsService.getTagGroupsAndData()
					.then((response: AxiosResponse<BioAdmin.ITagGroup[]>) => {
						this.tagGroups = response.data;
						this.setTags(BioServicesConstant.languagesName);
					});
			else
				return tagsMuleService.getTagGroupsAndData()
					.then((response: AxiosResponse<BioAdmin.ITagGroup[]>) => {
						this.tagGroups = response.data;
						this.setTags(BioServicesConstant.languagesName);
					});
		},
		getAssets(type: string, tags: string) {
			return assetsService.getAssetsByTag(type, tags)
				.then((response: AxiosResponse<BioAdmin.IContentAsset[]>) => {
					this.renderAsset(response.data);
				});
		},
		getRoleConfig() {
			return bioConfigService.getRoleConfig()
				.then((response: AxiosResponse<BioAdmin.IBiosConfigResponse>) => {
					this.biosConfigResponse = response.data;
				}).catch((error) => {
					// use config constant on localhost
					this.biosConfigResponse = RoleConfigs;
				});
		},
		renderAsset(data: BioAdmin.IContentAsset[]) {
			// get lead job title from profile and rename it to match expected content fragment name in AEM
			const leadTitle = _.filter(this.jobTitles, (jt: BioAdmin.IJobTitle) => jt.marketingTitle.indexOf('Manager') > -1);
			if (leadTitle && leadTitle.length > 0) {
				const titleName: string = leadTitle[0].marketingTitle.toLowerCase().replace(/ /g, '-');
				const asset: BioAdmin.IContentAsset[] = _.filter(data, (ca: BioAdmin.IContentAsset) => {
					return ca.name === titleName;
				});
				// get path to specific fragment from the asset path
				const fragmentPath = asset[0].path.split('content-fragments/');
				// send the fragment path section to the service to return the content and apply it to DOM
				return contentFragmentService.getContentFragment(fragmentPath[1])
					.then((response: AxiosResponse<any>) => {
						$('#responsibilitiesContent').html(response.data);
					});
			}
		},
		// set tags for dropdown lists
		setTags(name: string) {
			if (name === BioServicesConstant.languagesName) {
				const group: BioAdmin.ITagGroup | undefined = _.find(this.tagGroups, (tg: BioAdmin.ITagGroup) => tg.name === name);
				if (group) {
					this.languagesTags = _.orderBy(group.tags, 'name');
				}
			}
		},
		bindSharedEvents() {
			// need to bind shared-component events after content is loaded
			// @ts-ignore
			$('.multiselect').multiselect();
			// @ts-ignore
			$('[data-charcount]').charCount();
		},
		hasFeature(feature: string): boolean {
			return this.roleFeatures.includes(feature);
		},
		getQuoteMaxLengthValue(): number {
			if (this.biosConfigResponse.features.quote.maxLength) {
				return Number(this.biosConfigResponse.features.quote.maxLength);
			} else {
				return 0;
			}
		},
		hasRole(role: string): boolean {
			return this.roles.includes(role);
		},
		setFeaturesByRole() {
			this.roles = [];
			this.roleFeatures = [];
			// @ts-ignore
			_.forEach(this.bioProfile.roles, (profileRole: BioAdmin.IRole) => {
				const validRole: any = _.find(this.biosConfigResponse.rolesFeatures, (rc) => {
					return profileRole.name === rc.roleName;
				});
				this.roles.push(validRole.roleName);
				_.forEach(validRole.features, (feature) => {
					if (!this.roleFeatures.includes(feature)) {
						this.roleFeatures.push(feature);
					}
				});
			});
		},
		// HTML METHODS
		isClubInProfile(clubId: number) {
			return _.some(this.clubs, (club: BioAdmin.IProfileClub) => {
				return club.clubId === clubId;
			});
		},
		isLanguageInProfile(name: string) {
			if (this.languages && this.languages.length) {
				return _.some(this.languages, (tag: BioAdmin.ITag) => {
					return tag.name === name;
				});
			}
		},
		clubListModified(club: BioAdmin.IClub, event: Event): void {
			const data: BioAdmin.IProfileClub = {
				clubId: club.mmsClubId,
				name: club.name,
				selected: (event.currentTarget as HTMLInputElement).checked,
			};
			// @ts-ignore
			this.modifyProfileClubs(data);
        },
		languageListModified(tag: BioAdmin.ITag, event: any) {
			const data: BioAdmin.IListLanguage = {
				id: tag.id,
				name: tag.name,
				selected: (event.currentTarget as HTMLInputElement).checked,
			};
			// @ts-ignore
			this.modifyProfileLanguages(data);
		},
		removeClub(index: number): void {
			// @ts-ignore
			this.removeProfileClub(index);
		},
		removeLanguage(index: number): void {
			// @ts-ignore
			this.removeProfileLanguage(index);
		}
	},
	watch: {
		bioProfile() {
			// @ts-ignore
			this.clubs = this.getClubs;
			// @ts-ignore
			this.languages = this.getLanguages;
			// @ts-ignore
			this.jobTitles = this.getJobTitles;
			// @ts-ignore
			this.certifications = this.getCertifications;
		},
	},
	created(): void {
		EventHub.$on(EventConstant.BioProfileServiceLoading, (loading: boolean) => {
			this.bioProfileServiceLoading = loading;
		});
		// @ts-ignore
		Promise.all([this.synchronizeAlertNotificationStore(), this.synchronizeEmployeeProfileStore(), this.synchronizeUrlStore(), this.getRoleConfig()]).then(() => {
			const featureFlags = window.lt.features;
			if(featureFlags) {
				const feature = featureFlags[featureFlags.findIndex( (x: any) => x.featureId === 'biosSpringApi')];
				if(feature && feature.enabled)
				this.useSpringApi = true;
			}
			// @ts-ignore
			this.getProfile().then(() => {
				// get club list and populate dropdown
				this.getAllClubs();
				// populate and set tag groups
				this.getTagGroupsAndData();
				// set roles for user to determine section access
				this.setFeaturesByRole();
				if (this.hasFeature('responsibilities')) {
					this.getAssets(BioServicesConstant.contentFragmentType, BioServicesConstant.contentFragmentResponsibilitiesTag);
				}
				this.form = {
					// @ts-ignore
					quote: this.getQuote,
					// @ts-ignore
					quoteCitation: this.getQuoteCitation,
					// @ts-ignore
					aboutMe: this.getAboutMe,
					// @ts-ignore
					clubs: this.getClubs,
					// @ts-ignore
					avatarId: this.getAvatarId,
				};
				// enable shared component-specific event bindings
				setTimeout(this.bindSharedEvents, 500);
			});
		});
	},
	validations() {
		// build dynamic validation object based on role access for required fields
		const form: any = {};
		form['aboutMe'] = {
			required,
			maxLength: maxLength(1000),
			textValidate,
		};
		if (this.hasFeature('locations')) {
			form['clubs'] = {
				required,
			};
		}
		if (this.hasFeature('quote')) {
			form['quote'] = {
				maxLength: maxLength(Number(this.biosConfigResponse.features.quote.maxLength)),
				textValidate,
			};
		}

		return {
			form,
		};
	},
});
